import React, { useState, useCallback, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import {
    Box, Typography, TextField, Button, Alert, CircularProgress, Chip, Switch, FormControlLabel, Collapse,
} from '@mui/material';
import { SearchField } from '../Checkout';
import PinRequestDialog from './PinRequestDialog';
import PinCard from './PinCard';

const fmtDate = (iso) => {
    if (!iso) return '–';
    const d = new Date(iso);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
};

const fmtAge = (iso) => {
    if (!iso) return '';
    const diffMs = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Heute';
    if (days === 1) return 'vor 1 Tag';
    return `vor ${days} Tagen`;
};

const PinCustomerSearch = ({ posConfig, embedded }) => {
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const [selectedKdnr, setSelectedKdnr] = useState(null);
    const [sendungsnummer, setSendungsnummer] = useState('');
    const [pins, setPins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [requestDialogOpen, setRequestDialogOpen] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createFormData, setCreateFormData] = useState({ pin: '', sendungsnummer: '', spedition: '' });
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [createSuccess, setCreateSuccess] = useState(null);
    const [createError, setCreateError] = useState(null);
    const [showOld, setShowOld] = useState(false);
    const [showSidSearch, setShowSidSearch] = useState(false);
    const pinCardRefs = useRef({});

    const socketUrl = posConfig?.socketUrl || process.env.REACT_APP_WEBSOCKET_URL;

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        queryParams: { station: cookies.station, secret: cookies.secret, staff: cookies.staff },
        onMessage: (message) => handleWsEvent(message),
        shouldReconnect: () => true,
        retryOnError: true,
    });

    const handleWsEvent = useCallback((message) => {
        try {
            const msg = JSON.parse(message.data);
            if (msg.task === 'respSearchPinByTerm') {
                setLoading(false);
                if (msg.status === 'succeeded') {
                    setPins(msg.pins || []);
                    setError(null);
                } else {
                    setError(msg.message || 'Fehler bei der Suche.');
                }
                return;
            }
            if (msg.task === 'respCreatePinInternal') {
                setCreateSubmitting(false);
                if (msg.status === 'succeeded') {
                    setCreateSuccess('PIN erfasst!');
                    setCreateError(null);
                    setCreateFormData({ pin: '', sendungsnummer: '', spedition: '' });
                    setShowCreateForm(false);
                    // Refresh search results
                    if (selectedKdnr) doSearch(selectedKdnr);
                } else {
                    setCreateError(msg.message || 'Fehler beim Erstellen.');
                }
                return;
            }
            // Forward pin-specific messages to PinCard components
            const fwd = ['respRevealPin', 'respRedeemPin'];
            if (fwd.includes(msg.task)) {
                Object.values(pinCardRefs.current).forEach((h) => {
                    if (typeof h === 'function') h(msg);
                });
            }
        } catch (e) {
            console.error('WS parse error:', e);
        }
    }, [selectedKdnr]); // eslint-disable-line react-hooks/exhaustive-deps

    const doSearch = useCallback((term) => {
        if (!term) return;
        setLoading(true);
        setPins([]);
        setError(null);
        sendMessage(JSON.stringify({
            task: 'searchPinByTerm',
            station: cookies.station,
            staff: cookies.staff,
            term: String(term),
        }));
    }, [sendMessage, cookies.station, cookies.staff]);

    const handleKdnrSelect = (kdnr) => {
        setSelectedKdnr(String(kdnr));
        doSearch(kdnr);
    };

    const handleSendungsnummerSearch = (e) => {
        e.preventDefault();
        if (sendungsnummer.trim()) {
            doSearch(sendungsnummer.trim());
        }
    };

    const handleCreateSubmit = () => {
        if (!selectedKdnr) { setCreateError('Bitte zuerst einen Kunden auswählen.'); return; }
        if (!createFormData.pin.trim()) { setCreateError('PIN ist ein Pflichtfeld.'); return; }
        setCreateError(null);
        setCreateSuccess(null);
        setCreateSubmitting(true);
        const payload = {
            task: 'createPinInternal',
            station: cookies.station,
            staff: cookies.staff,
            kdnr: Number(selectedKdnr),
            pin: createFormData.pin.trim(),
        };
        if (createFormData.sendungsnummer.trim()) payload.sendungsnummer = createFormData.sendungsnummer.trim();
        if (createFormData.spedition.trim()) payload.spedition = createFormData.spedition.trim();
        sendMessage(JSON.stringify(payload));
    };

    const isConnected = readyState === ReadyState.OPEN;

    // Separate: pending requests (ANFORDERUNG without PIN) vs all other pins
    const visiblePins = showOld ? pins : pins.filter(p => p.status === 'AKTIV');
    const activeRequests = visiblePins.filter(
        (p) => p.status === 'AKTIV' && p.type === 'ANFORDERUNG' && (!p.pin || !p.pin.trim())
    );
    const pinsWithContent = visiblePins.filter(
        (p) => !(p.status === 'AKTIV' && p.type === 'ANFORDERUNG' && (!p.pin || !p.pin.trim()))
    );

    return (
        <Box sx={{ maxWidth: 800 }}>
            {!embedded && (
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                    PIN-Suche
                </Typography>
            )}

            {/* Customer search via SearchField from Checkout */}
            <Box sx={{ mb: 2 }}>
                <SearchField
                    posConfig={posConfig}
                    onSelect={handleKdnrSelect}
                    PaperComponent={Box}
                    paperSx={{ p: 0 }}
                    noAutofocus
                />
                {selectedKdnr && (
                    <Alert severity="info" sx={{ py: 0, mt: 1 }}>
                        Ausgewählter Kunde: <strong>{selectedKdnr}</strong>
                        {pins.length > 0 && pins[0].kundenName && (
                            <> – {pins[0].kundenName}</>
                        )}
                    </Alert>
                )}
            </Box>

            {/* Separate Sendungsnummer search */}
            <Box sx={{ mb: 2 }}>
                <Button size="small" variant="text" onClick={() => setShowSidSearch(!showSidSearch)}
                    sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, color: '#777', p: 0, mb: 0.5 }}>
                    {showSidSearch ? '▾' : '▸'} Sendungsnummer-Suche
                </Button>
                <Collapse in={showSidSearch}>
                    <Box component="form" onSubmit={handleSendungsnummerSearch} sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            placeholder="Sendungsnummer eingeben"
                            value={sendungsnummer}
                            onChange={(e) => setSendungsnummer(e.target.value)}
                            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 13 } }}
                        />
                        <Button
                            type="submit"
                            variant="outlined"
                            size="small"
                            disabled={!isConnected || loading || !sendungsnummer.trim()}
                            sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#ddd', color: '#1a1a1a' }}
                        >
                            Suchen
                        </Button>
                    </Box>
                </Collapse>
            </Box>

            {/* Toggle aktiv/alte */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <FormControlLabel
                    control={
                        <Switch size="small" checked={showOld}
                            onChange={(e) => setShowOld(e.target.checked)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a1a1a' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1a1a1a' } }}
                        />
                    }
                    label={<Typography sx={{ fontSize: 12, color: '#777', fontWeight: 600 }}>Auch eingelöste anzeigen</Typography>}
                    sx={{ ml: 0 }}
                />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {createSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{createSuccess}</Alert>}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {/* "+ Hinzufügen" card — always visible when a customer is selected */}
            {!loading && selectedKdnr && (
                <Box sx={{ mb: 2 }}>
                    {!showCreateForm ? (
                        <Box
                            onClick={() => { setShowCreateForm(true); setCreateError(null); setCreateSuccess(null); }}
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                                px: 2.5, py: 2, borderRadius: '10px',
                                border: '2px dashed #ccc', cursor: 'pointer',
                                bgcolor: '#fafafa', transition: 'all 0.15s',
                                '&:hover': { borderColor: '#999', bgcolor: '#f0f0f0' },
                            }}
                        >
                            <Typography sx={{ fontSize: 20, fontWeight: 300, color: '#999' }}>+</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#777' }}>
                                PIN hinzufügen
                            </Typography>
                            <Chip label="Neu" size="small"
                                sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#1a1a1a', color: '#fff', ml: 0.5 }} />
                        </Box>
                    ) : (
                        <Box sx={{
                            px: 2.5, py: 2, borderRadius: '10px',
                            border: '1px solid #e5e5e3', bgcolor: '#fff',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
                                    PIN für Kunde {selectedKdnr} erfassen
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                <Button size="small" onClick={() => setShowCreateForm(false)}
                                    sx={{ textTransform: 'none', fontSize: 11, color: '#999', minWidth: 0 }}>
                                    Abbrechen
                                </Button>
                            </Box>
                            {createError && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, py: 0 }}>{createError}</Alert>}
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <TextField label="PIN *" size="small" value={createFormData.pin}
                                    onChange={(e) => setCreateFormData(prev => ({ ...prev, pin: e.target.value }))}
                                    sx={{ flex: '1 1 120px', '& .MuiInputBase-input': { fontSize: 13 } }} />
                                <TextField label="Sendungsnr." size="small" value={createFormData.sendungsnummer}
                                    onChange={(e) => setCreateFormData(prev => ({ ...prev, sendungsnummer: e.target.value }))}
                                    sx={{ flex: '1 1 140px', '& .MuiInputBase-input': { fontSize: 13 } }} />
                                <TextField label="Spedition" size="small" value={createFormData.spedition}
                                    onChange={(e) => setCreateFormData(prev => ({ ...prev, spedition: e.target.value }))}
                                    sx={{ flex: '1 1 120px', '& .MuiInputBase-input': { fontSize: 13 } }} />
                                <Button variant="contained" size="small" disableElevation
                                    disabled={createSubmitting || !isConnected}
                                    onClick={handleCreateSubmit}
                                    sx={{
                                        borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600,
                                        bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' }, height: 40,
                                    }}>
                                    {createSubmitting ? <CircularProgress size={16} /> : 'Speichern'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* Display active PIN requests (ANFORDERUNG without PIN) */}
            {!loading && activeRequests.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                        Noch keine Kunden-Antwort erhalten
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {activeRequests.map((req) => (
                            <Box key={req.pinRecordId} sx={{
                                px: 2, py: 1.5, borderRadius: 2, bgcolor: '#fff',
                                border: '1px solid #fef3c7',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    {req.sendungsnummer && (
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{req.sendungsnummer}</Typography>
                                    )}
                                    {req.spedition && (
                                        <Chip label={req.spedition} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#e5e5e3' }} />
                                    )}
                                    <Typography sx={{ fontSize: 11, color: '#999' }}>{fmtDate(req.erstelltAm)}</Typography>
                                    <Chip label={fmtAge(req.erstelltAm)} size="small"
                                        sx={{ height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Display PINs with PinCard (Freirubbeln / Erledigt) */}
            {!loading && pinsWithContent.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                        Gespeicherte PIN's
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {pinsWithContent.map((p) => (
                            <PinCard
                                key={p.pinRecordId}
                                pin={p}
                                sendMessage={sendMessage}
                                cookies={cookies}
                                registerMessageHandler={(h) => { pinCardRefs.current[p.pinRecordId] = h; }}
                                unregisterMessageHandler={() => { delete pinCardRefs.current[p.pinRecordId]; }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* No results message */}
            {!loading && pins.length === 0 && (selectedKdnr || sendungsnummer.trim()) && !error && (
                <Typography sx={{ py: 2, textAlign: 'center', color: '#999', fontSize: 13 }}>
                    Keine aktiven PIN's gefunden.
                </Typography>
            )}

            {/* "PIN anfordern" button */}
            {!loading && selectedKdnr && (
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        size="small"
                        disableElevation
                        onClick={() => setRequestDialogOpen(true)}
                        disabled={!isConnected}
                        sx={{
                            borderRadius: 2, textTransform: 'none', fontSize: 13, fontWeight: 600,
                            bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' },
                        }}
                    >
                        PIN anfordern
                    </Button>
                </Box>
            )}

            {/* PinRequestDialog */}
            <PinRequestDialog
                open={requestDialogOpen}
                onClose={() => setRequestDialogOpen(false)}
                kdnr={selectedKdnr}
                posConfig={posConfig}
                onSuccess={() => {
                    setRequestDialogOpen(false);
                    if (selectedKdnr) doSearch(selectedKdnr);
                }}
            />
        </Box>
    );
};

export default PinCustomerSearch;
